import { Stat } from "@/components/home/panel";
import { RankPanel } from "@/components/home/RankPanel";
import {
  ExceptionPanel,
  MoneyPanel,
  MutualLinkNotice,
  PipelinePanel,
  PresencePanel,
  ReportsPanel,
  RosterPanel,
} from "@/pages/home/blocks";
import { fmtGel } from "@/lib/money";
import type { ApexData, HomeViewer } from "@/pages/home/types";

/** Shareholder + executive manager.
 *
 *  Their page access is identical (25 pages each), so the difference is
 *  emphasis, not content: a shareholder is asked to watch the money, an
 *  executive manager to run the operation. The genuinely different *data* —
 *  whether the shareholder tier appears in the roster and presence — falls out
 *  of `hidden_roles_for` on the server and needs no branch here.
 *
 *  No goal ring anywhere: nobody at this tier has ever published a listing.
 */
export function ApexHome({
  data,
  viewer,
  allowed,
}: {
  data: ApexData;
  viewer: HomeViewer;
  allowed: Set<string>;
}) {
  const goal = data.team.producers ? data.team.goal_today / data.team.producers : 0;
  const moneyFirst = viewer.role === "shareholder";
  const mutualNames = data.direct_reports
    .filter((p) => data.mutual_manager_ids.includes(p.id))
    .map((p) => p.full_name || p.email);

  const failPct = Math.round(data.pipeline.error_rate * 100);

  // "N of M on target", never a summed goal bar: with 40/day and ten producers
  // the agency total is 400, which almost never gets close — a summed ring
  // would read permanently broken and tell a principal nothing to act on.
  const kpis = [
    <Stat
      key="target"
      label="On target today"
      value={data.team.on_target}
      suffix={`of ${data.team.producers}`}
      bar={data.team.producers ? data.team.on_target / data.team.producers : undefined}
      tone={data.team.on_target > 0 ? "emerald" : "amber"}
      foot="Brokers at or above the daily goal"
    />,
    <Stat
      key="published"
      label="Published today"
      value={data.team.published_today.toLocaleString()}
      foot="Across the whole team"
      to={allowed.has("/placements") ? "/placements" : undefined}
    />,
    <Stat
      key="deals"
      label="Deals closed"
      value={data.money.deals_done}
      foot="This month"
      tone="emerald"
      to={allowed.has("/deals") ? "/deals" : undefined}
    />,
    <Stat
      key="fail"
      label="Publish failures"
      value={`${failPct}%`}
      foot={`${data.pipeline.error + data.pipeline.cancelled} of ${
        data.pipeline.done + data.pipeline.error + data.pipeline.cancelled
      } runs · 7d`}
      tone={failPct >= 20 ? "rose" : failPct >= 10 ? "amber" : "emerald"}
      to={allowed.has("/admin/jobs") ? "/admin/jobs" : undefined}
    />,
  ];

  // A shareholder leads with money; the executive manager leads with output.
  if (moneyFirst) {
    const net = data.money.commission_gel - data.money.expenses_gel;
    kpis.unshift(
      <Stat
        key="money"
        label="Commission this month"
        // One figure in GEL, the agency's reporting currency — the same
        // normalisation the Finance page uses. Showing $ and ₾ side by side
        // asked the reader to add two currencies in their head.
        value={fmtGel(String(Math.round(data.money.commission_gel)))}
        tone="emerald"
        foot={
          data.money.fx_complete
            ? `${fmtGel(String(Math.round(net)))} after ${fmtGel(
                String(Math.round(data.money.expenses_gel)),
              )} costs`
            : "Some deals have no FX rate — total understates"
        }
        to={allowed.has("/finance") ? "/finance" : undefined}
      />,
    );
    kpis.pop();
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{kpis}</div>

      {/* Two independently-flowing columns. Previously these were two separate
          grid ROWS, so the short left column had to wait for the tall right one
          before the roster could start — ~300px of dead space mid-page. */}
      <div className="grid gap-3 lg:grid-cols-3 items-start">
        <div className="lg:col-span-2 min-w-0 space-y-3">
          <ExceptionPanel rows={data.exceptions} />
          <MutualLinkNotice names={mutualNames} />
          <RosterPanel rows={data.roster} goalDaily={goal} />
          <ReportsPanel people={data.direct_reports} />
        </div>
        <div className="space-y-3 min-w-0">
          {!moneyFirst && allowed.has("/finance") && <MoneyPanel money={data.money} />}
          <PipelinePanel p={data.pipeline} />
          <PresencePanel p={data.presence} to={allowed.has("/audit") ? "/audit?tab=people" : undefined} />
          <RankPanel />
        </div>
      </div>
    </div>
  );
}
